import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const AuthContext = createContext();
const API_BASE = API_BASE_URL;
const API = `${API_BASE}/api`;

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  // Configure axios defaults and interceptors
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setToken(token);
    }
    
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Check existing token
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
        } catch (authError) {
          console.error('Auth verification failed:', authError);
          // Only logout if it's clearly an auth failure (401/403), not network/server errors
          if (authError.response?.status === 401 || authError.response?.status === 403) {
            console.warn('Token invalid or expired, logging out');
            logout();
          } else {
            console.warn('Auth check failed due to network/server error, keeping user logged in');
            // Keep the user logged in but don't set user data
          }
        }
      }
    } catch (error) {
      console.error('Auth check error:', error);
      // Don't automatically logout on unexpected errors
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async (sessionId) => {
    try {
      const response = await axios.post(`${API}/auth/google/session`, {
        session_id: sessionId
      });

      if (response.data.success) {
        const { access_token, session_token, user } = response.data;
        
        // Store tokens
        localStorage.setItem('access_token', access_token);
        setToken(access_token);
        setUser(user);
        
        // Set axios default header
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        
        // Set session cookie (handled by browser)
        document.cookie = `session_token=${session_token}; path=/; HttpOnly; Secure; SameSite=None`;
      }
    } catch (error) {
      console.error('Google auth failed:', error);
      throw error;
    }
  };

  const login = async (email, password, totpCode = null) => {
    try {
      const payload = { email, password };
      if (totpCode) {
        payload.totp_code = totpCode;
      }
      
      const response = await axios.post(`${API}/auth/login`, payload);

      // Check if 2FA is required
      if (response.data.requires_2fa) {
        return { success: false, requires_2fa: true };
      }

      const { access_token, user } = response.data;
      
      // Store token
      localStorage.setItem('access_token', access_token);
      setToken(access_token);
      setUser(user);
      
      // Set axios default header
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setToken(null);
  };

  const hasRole = (requiredRoles) => {
    if (!user) return false;
    if (typeof requiredRoles === 'string') requiredRoles = [requiredRoles];
    return requiredRoles.includes(user.role);
  };

  const hasAnyRole = (roles) => {
    return roles.some(role => hasRole(role));
  };

  const value = {
    user,
    login,
    logout,
    loading,
    token,
    hasRole,
    hasAnyRole,
    isAdmin: () => hasRole('admin'),
    isManager: () => hasAnyRole(['admin', 'manager']),
    isStaff: () => hasAnyRole(['admin', 'manager', 'staff']),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}